export type RegistrationVerificationState =
  | 'approved'
  | 'in_review'
  | 'correction'
  | 'pending'
  | 'rejected';

export type RegistrationEntityReviewStatus =
  | 'draft'
  | 'pending_review'
  | 'in_review'
  | 'need_revision'
  | 'approved';

export interface RegistrationVerificationPresentation {
  description: string;
  badgeLabel: string;
}

const PRESENTATION: Record<
  RegistrationVerificationState,
  RegistrationVerificationPresentation
> = {
  approved: {
    description: 'Selesai diverifikasi',
    badgeLabel: 'Disetujui',
  },
  in_review: {
    description: 'Dalam proses verifikasi',
    badgeLabel: 'Ditinjau',
  },
  correction: {
    description: 'Diperlukan perbaikan Data',
    badgeLabel: 'Koreksi',
  },
  pending: {
    description: 'Menunggu verifikasi',
    badgeLabel: 'Menunggu',
  },
  rejected: {
    description: 'Pengajuan ditolak',
    badgeLabel: 'Ditolak',
  },
};

export const REGISTRATION_VERIFICATION_BADGE_STYLES: Record<
  RegistrationVerificationState,
  { bg: string; color: string }
> = {
  approved: { bg: '#F0FDF4', color: '#008236' },
  in_review: { bg: '#F1F8FF', color: '#0941A8' },
  correction: { bg: '#FFF9E1', color: '#BB4D00' },
  pending: { bg: '#FAFAFA', color: '#637381' },
  rejected: { bg: '#FEF2F2', color: '#EE0707' },
};

export function getRegistrationVerificationPresentation(
  state: RegistrationVerificationState
): RegistrationVerificationPresentation {
  return PRESENTATION[state];
}

export function isRejectedEntityStatus(
  status?: string | null
): status is 'need_revision' {
  return status === 'need_revision';
}

export function mapDraftReviewStatus(
  status?: RegistrationEntityReviewStatus | null
): RegistrationVerificationState {
  if (status === 'need_revision') {
    return 'rejected';
  }

  if (
    status === 'pending_review' ||
    status === 'in_review' ||
    status === 'approved'
  ) {
    return 'in_review';
  }

  return 'pending';
}

function mapPendingReviewSectionState(
  section: {
    complete: boolean;
    status?: RegistrationEntityReviewStatus | null;
    missingCount: number;
  },
  options: { treatCompleteAsApproved: boolean }
): RegistrationVerificationState {
  if (isRejectedEntityStatus(section.status)) {
    return 'rejected';
  }

  if (section.missingCount > 0) {
    return 'correction';
  }

  if (section.complete) {
    return options.treatCompleteAsApproved ? 'approved' : 'in_review';
  }

  if (section.status === 'pending_review' || section.status === 'in_review') {
    return 'in_review';
  }

  return 'pending';
}

function isRegistrationCompletedStatus(registrationStatus: string): boolean {
  return registrationStatus === 'completed';
}

type RegistrationProgressRegistrationStatus =
  | 'otp_verified'
  | 'pending_review'
  | 'in_review'
  | 'need_revision'
  | 'approved'
  | 'completed';

function isPostSubmitRegistrationStatus(
  registrationStatus: RegistrationProgressRegistrationStatus
): boolean {
  return (
    registrationStatus === 'pending_review' ||
    registrationStatus === 'in_review' ||
    registrationStatus === 'need_revision'
  );
}

export function mapBusinessSectionToVerificationState(
  registrationStatus: RegistrationProgressRegistrationStatus,
  businessSection: {
    complete: boolean;
    status?: RegistrationEntityReviewStatus | null;
    missing_fields: string[];
    missing_documents: string[];
  },
  options: { treatCompleteAsApproved?: boolean } = {}
): RegistrationVerificationState {
  const treatCompleteAsApproved = options.treatCompleteAsApproved ?? false;
  const sectionInput = {
    complete: businessSection.complete,
    status: businessSection.status,
    missingCount:
      businessSection.missing_fields.length +
      businessSection.missing_documents.length,
  };

  if (isRegistrationCompletedStatus(registrationStatus)) {
    return mapPendingReviewSectionState(sectionInput, {
      treatCompleteAsApproved: true,
    });
  }

  if (!isPostSubmitRegistrationStatus(registrationStatus)) {
    return mapDraftReviewStatus(businessSection.status);
  }

  return mapPendingReviewSectionState(sectionInput, {
    treatCompleteAsApproved,
  });
}

export function mapOutletSectionToVerificationState(
  registrationStatus: RegistrationProgressRegistrationStatus,
  outletSection: {
    complete: boolean;
    status: RegistrationEntityReviewStatus | null;
    missing_fields: string[];
    missing_attachments: string[];
  },
  options: { treatCompleteAsApproved?: boolean } = {}
): RegistrationVerificationState {
  const treatCompleteAsApproved = options.treatCompleteAsApproved ?? false;
  const sectionInput = {
    complete: outletSection.complete,
    status: outletSection.status,
    missingCount:
      outletSection.missing_fields.length +
      outletSection.missing_attachments.length,
  };

  if (isRegistrationCompletedStatus(registrationStatus)) {
    return mapPendingReviewSectionState(sectionInput, {
      treatCompleteAsApproved: true,
    });
  }

  if (!isPostSubmitRegistrationStatus(registrationStatus)) {
    if (isRejectedEntityStatus(outletSection.status)) {
      return 'rejected';
    }

    return outletSection.complete ? 'in_review' : 'pending';
  }

  return mapPendingReviewSectionState(sectionInput, {
    treatCompleteAsApproved,
  });
}

export function getOutletCorrectionMessage(outletSection: {
  missing_fields: string[];
  missing_attachments: string[];
}): string | null {
  const missingCount =
    outletSection.missing_fields.length +
    outletSection.missing_attachments.length;
  if (missingCount <= 0) {
    return null;
  }

  return `${missingCount} Dokumen perlu diperbaiki`;
}

export function getBusinessFieldsCorrectionMessage(businessSection: {
  missing_fields: string[];
}): string | null {
  if (businessSection.missing_fields.length <= 0) {
    return null;
  }

  return 'Diperlukan perbaikan Data';
}

export function getBusinessDocumentsCorrectionMessage(businessSection: {
  missing_documents: string[];
}): string | null {
  const missingCount = businessSection.missing_documents.length;
  if (missingCount <= 0) {
    return null;
  }

  return `${missingCount} Dokumen perlu diperbaiki`;
}

export function getBusinessCorrectionMessage(businessSection: {
  missing_fields: string[];
  missing_documents: string[];
}): string | null {
  const fieldsMessage = getBusinessFieldsCorrectionMessage(businessSection);
  const documentsMessage =
    getBusinessDocumentsCorrectionMessage(businessSection);

  if (fieldsMessage && documentsMessage) {
    return `${fieldsMessage}; ${documentsMessage}`;
  }

  return fieldsMessage ?? documentsMessage;
}

export function getBusinessSectionDescription(
  registrationStatus: RegistrationProgressRegistrationStatus,
  businessSection: {
    complete: boolean;
    missing_fields: string[];
    missing_documents: string[];
  },
  state: RegistrationVerificationState
): string {
  if (state === 'rejected') {
    return 'Pengajuan ditolak';
  }

  if (registrationStatus === 'pending_review' && businessSection.complete) {
    return 'Selesai diverifikasi';
  }

  return getRegistrationVerificationPresentation(state).description;
}

export function getOutletSectionDescription(
  registrationStatus: RegistrationProgressRegistrationStatus,
  outletSection: {
    complete: boolean;
  },
  state: RegistrationVerificationState
): string {
  if (state === 'rejected') {
    return 'Pengajuan ditolak';
  }

  if (registrationStatus === 'pending_review' && outletSection.complete) {
    return 'Dalam proses verifikasi';
  }

  return getRegistrationVerificationPresentation(state).description;
}
